import type { ShopProduct, ProductColorOption } from '../mocks/shopProducts';
import type { SelectedOptions } from '../pages/product-detail/types';
import { fitLabelToEnum } from './cartItemConfiguration';
import { loadRemoteCatalogSnapshot } from './catalogCache';
import { isAppearanceSelectionOptionalProductCategory } from '../pages/product-detail/fitRequirement';
import {
  createAuthorizedHeaders,
  fetchAwsApi,
  getApiErrorMessage,
  getSessionAccountId,
  isAuthenticatedSession,
  parseApiResponse,
  sanitizeGenericText,
} from '../security';

export interface CustomizationSpecsPayload {
  store?: string;
  session_id?: string;
  item_id: string;
  cart_item_id?: string;
  size_value?: string;
  size?: string;
  fit?: string | null;
  fabric_blend?: string | null;
  color?: string | null;
  quantity?: number;
}

export interface CustomizationSpecsCartItem {
  id?: string;
  item_id?: string;
  quantity?: number;
  measurements?: Record<string, unknown>;
  size?: string;
  s3_key?: string;
  s3Key?: string;
  fit?: string;
  color?: string;
  material?: Record<string, unknown>;
  gender?: string;
  fabric_blend?: string;
  fabricBlend?: string;
  unit_price?: number | string;
  unitPrice?: number | string;
  base_price?: number | string;
  basePrice?: number | string;
  price?: number | string;
}

const STYLE_CODE_BY_PRODUCT_ID: Record<string, string> = {
  p13: 'PoloT01',
  p14: 'PoloT04',
  p15: 'DBV6751',
  p16: 'SAW2302',
};

function isOneSizeValue(value: string) {
  return sanitizeGenericText(value, 24).trim().toLowerCase() === 'one size';
}

function hasSelectableSizeOptions(product: ShopProduct) {
  return (product.optionGroups ?? []).some((group) => (
    group.type === 'button'
    && group.key === 'size'
    && group.options.some((option) => !isOneSizeValue(option.value))
  ));
}

function getSelectedButtonValue(selectedOptions: SelectedOptions, key: string) {
  const value = selectedOptions[key];
  return typeof value === 'string' ? sanitizeGenericText(value, 80) : '';
}

function getSelectedColorValue(selectedOptions: SelectedOptions, key: string) {
  const value = selectedOptions[key];

  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return sanitizeGenericText(value, 80);
  }

  const colorOption = value as ProductColorOption;

  // Prefer color name (label) so the backend receives a human-readable color name
  if (typeof colorOption.label === 'string') {
    const label = sanitizeGenericText(colorOption.label, 80);
    if (label) return label;
  }

  if (colorOption.label && typeof colorOption.label === 'object') {
    const englishLabel = 'en' in colorOption.label ? sanitizeGenericText(String(colorOption.label.en ?? ''), 80) : '';
    if (englishLabel) return englishLabel;

    const chineseLabel = 'zh' in colorOption.label ? sanitizeGenericText(String(colorOption.label.zh ?? ''), 80) : '';
    if (chineseLabel) return chineseLabel;
  }

  // Fall back to code
  const normalizedCode = sanitizeGenericText(colorOption.code ?? '', 80);
  if (normalizedCode) return normalizedCode;

  return '';
}

function normalizeInventoryToken(value: unknown) {
  return sanitizeGenericText(String(value ?? ''), 80).trim().toLowerCase();
}

function getProductInventoryRows(product: ShopProduct) {
  const productRecord = product as ShopProduct & Record<string, unknown>;
  const adminPayload = product.adminItemUpdatePayload ?? {};
  const candidates = [
    product.inventoryRows,
    productRecord.inventory_rows,
    adminPayload.inventory_rows,
    adminPayload.inventoryRows,
  ];

  return candidates.flatMap((candidate) => (
    Array.isArray(candidate)
      ? candidate.filter((row): row is Record<string, unknown> => (
        typeof row === 'object' && row !== null && !Array.isArray(row)
      ))
      : []
  ));
}

export function getInventoryFabricCodeForSelection(
  product: ShopProduct,
  selectedOptions: SelectedOptions
) {
  const selectedFabric = getSelectedButtonValue(selectedOptions, 'fabric').trim();
  const selectedColor = selectedOptions.color;
  if (!selectedColor) {
    return selectedFabric;
  }

  const selectedColorCode = typeof selectedColor === 'string'
    ? normalizeInventoryToken(selectedColor)
    : normalizeInventoryToken(selectedColor.code);
  const selectedColorName = normalizeInventoryToken(getSelectedColorValue(selectedOptions, 'color'));
  const selectedSize = normalizeInventoryToken(getSelectedButtonValue(selectedOptions, 'size'));
  const matchingColorRows = getProductInventoryRows(product).filter((row) => {
    const rowColorCode = normalizeInventoryToken(row.color_code ?? row.colorCode);
    const rowColorName = normalizeInventoryToken(row.color ?? row.color_name ?? row.colorName);

    return Boolean(
      (selectedColorCode && rowColorCode === selectedColorCode)
      || (selectedColorName && rowColorName === selectedColorName)
      || (selectedColorCode && rowColorName === selectedColorCode)
      || (selectedColorName && rowColorCode === selectedColorName)
    );
  });
  const selectedSizeRow = selectedSize
    ? matchingColorRows.find((row) => (
      normalizeInventoryToken(row.size_value ?? row.sizeValue ?? row.accessories_size ?? row.accessoriesSize)
        === selectedSize
    ))
    : undefined;
  const matchingRow = selectedSizeRow ?? matchingColorRows[0];

  const inventoryFabric = sanitizeGenericText(
    String(matchingRow?.fabric_code ?? matchingRow?.fabricCode ?? ''),
    80
  ).trim();

  return inventoryFabric || selectedFabric;
}

function isUuid(value: string | undefined) {
  if (!value) {
    return false;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function getRemoteStyleCode(item: unknown) {
  if (!item || typeof item !== 'object') {
    return '';
  }

  const candidate = item as Record<string, unknown>;
  const nestedItem = candidate.item && typeof candidate.item === 'object'
    ? (candidate.item as Record<string, unknown>)
    : null;
  const nestedData = candidate.data && typeof candidate.data === 'object'
    ? (candidate.data as Record<string, unknown>)
    : null;

  const styleValue =
    candidate.style
    ?? candidate.style_code
    ?? candidate.styleCode
    ?? nestedItem?.style
    ?? nestedItem?.style_code
    ?? nestedItem?.styleCode
    ?? nestedData?.style
    ?? nestedData?.style_code
    ?? nestedData?.styleCode;

  return typeof styleValue === 'string' ? styleValue.trim() : '';
}

async function resolveCustomizationItemId(product: ShopProduct, style: string) {
  if (isUuid(product.id)) {
    return product.id;
  }

  if (!style) {
    return product.id;
  }

  const snapshot = await loadRemoteCatalogSnapshot({
    errorMessage: 'Unable to load the production catalog for this customization.',
  });

  const matchingRemoteItem = snapshot.remoteItems.find((item) => {
    const itemId = typeof item.id === 'string' ? item.id.trim() : '';
    return isUuid(itemId) && getRemoteStyleCode(item) === style;
  });

  const resolvedItemId = typeof matchingRemoteItem?.id === 'string' ? matchingRemoteItem.id.trim() : '';

  if (!isUuid(resolvedItemId)) {
    throw new Error(
      'This customizable product is not linked to a production catalog item yet. Please sync the matching item before ordering.'
    );
  }

  return resolvedItemId;
}

export async function buildCustomizationSpecsPayload(
  product: ShopProduct,
  selectedOptions: SelectedOptions,
  options?: {
    cartItemId?: string;
    quantity?: number;
  }
): Promise<CustomizationSpecsPayload> {
  const style = product.styleCode || STYLE_CODE_BY_PRODUCT_ID[product.id];
  const hasFitOptions = (product.optionGroups ?? []).some((g) => g.key === 'fit');
  const hasSelectableSizes = hasSelectableSizeOptions(product);
  const selectedSize = getSelectedButtonValue(selectedOptions, 'size');
  const accessorySize = sanitizeGenericText(product.accessoriesSize ?? '', 24);
  const size = hasSelectableSizes ? selectedSize : (accessorySize || selectedSize);
  const selectedFit = getSelectedButtonValue(selectedOptions, 'fit');
  const fit = fitLabelToEnum(selectedFit);
  const color = getSelectedColorValue(selectedOptions, 'color');
  const sessionId = isAuthenticatedSession()
    ? ''
    : sanitizeGenericText(getSessionAccountId(null), 80);

  if (hasSelectableSizes && !size) {
    throw new Error('Please choose a size before ordering.');
  }

  if (hasFitOptions && !fit && !isAppearanceSelectionOptionalProductCategory(product.category)) {
    throw new Error('Please choose a fit before ordering.');
  }

  const hasColorOptions = (product.optionGroups ?? []).some((g) => g.key === 'color');
  if (hasColorOptions && !color && !isAppearanceSelectionOptionalProductCategory(product.category)) {
    throw new Error('Please choose a color before ordering.');
  }

  const itemId = await resolveCustomizationItemId(product, style);

  return {
    store: __STORE_NAME__,
    ...(sessionId ? { session_id: sessionId } : {}),
    item_id: itemId,
    ...(options?.cartItemId ? { cart_item_id: options.cartItemId } : {}),
    ...(size ? { size_value: size } : {}),
    fit: fit ?? null,
    color: isAppearanceSelectionOptionalProductCategory(product.category) ? null : (color || null),
    ...(typeof options?.quantity === 'number' && Number.isInteger(options.quantity) && options.quantity > 0
      ? { quantity: options.quantity }
      : {}),
  };
}

export async function enqueueCustomizationSpecs(payload: CustomizationSpecsPayload) {
  const guestSessionId = sanitizeGenericText(payload.session_id ?? '', 80);
  const requestPayload = {
    queue: 'customization_specs',
    ...payload,
  };

  const response = await fetchAwsApi('/api/enqueue_tasks', {
    method: 'POST',
    headers: createAuthorizedHeaders({
      'Content-Type': 'application/json',
      ...(guestSessionId ? { 'X-Session-ID': guestSessionId } : {}),
    }),
    body: JSON.stringify(requestPayload),
  });

  const responseBody = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(responseBody, 'Unable to save your customization.'));
  }

  return responseBody as { request_id?: string; status?: string };
}
